<!--
  Harvesting 2.0 — Qualité & synchro.

  Chaque contrôle est une façon dont du volume peut disparaître d'un total sans
  que personne ne le voie. C'est le défaut que toute cette section existe pour
  supprimer, donc les contrôles sont montrés plutôt qu'appliqués en silence.

  CE QUI CHANGE
  L'écran empilait cinq tableaux copiés-collés, sans état vide : un jeu de
  données SAIN affichait cinq tableaux vides, strictement indiscernables d'un
  échec de requête. Ici chaque contrôle est une ligne qui dit son verdict, et ne
  se déplie que s'il a quelque chose à montrer.

  Il gagne aussi le JOURNAL DE SYNCHRO, alimenté par un endpoint livré et resté
  sans consommateur. Le commentaire de la migration 045 est explicite : c'est
  l'absence de journal qui a laissé une trentaine d'avize disparaître sans trace.
-->
<template>
  <div>
    <!-- ===================== LES CONTRÔLES ===================== -->
    <vx-card class="mb-base">
      <hv-section
        title="Les contrôles de données"
        sub="Chaque ligne est un chemin par lequel du volume peut sortir des totaux sans erreur visible. Un contrôle au vert n'a rien à déplier.">
        <template slot="actions">
          <hv-pill :tone="failing ? 'warn' : 'ok'"
                   :icon="failing ? 'AlertTriangleIcon' : 'CheckCircleIcon'"
                   :label="failing ? `${failing} contrôle(s) à regarder` : 'Tout est conforme'" />
        </template>

        <div v-if="loading" class="hv2-loading">
          <vs-progress indeterminate color="primary" class="mb-2" />Chargement…
        </div>
        <div v-else-if="error" class="hv2-error">
          <b>Les contrôles n'ont pas pu être chargés.</b><br />{{ error }}
        </div>

        <div v-else>
          <div v-for="c in checks" :key="c.key" class="hv2-kv">
            <div class="flex items-center justify-between"
                 :class="c.rows.length ? 'cursor-pointer' : ''"
                 role="button" tabindex="0"
                 @click="c.rows.length && toggle(c.key)" @keyup.enter="c.rows.length && toggle(c.key)">
              <div>
                <hv-pill :tone="c.rows.length ? 'danger' : 'ok'"
                         :icon="c.rows.length ? 'AlertCircleIcon' : 'CheckIcon'"
                         :label="c.rows.length ? `${c.rows.length}` : 'conforme'" />
                <span class="ml-2">{{ c.label }}</span>
              </div>
              <feather-icon v-if="c.rows.length"
                            :icon="open[c.key] ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                            svgClasses="w-4 h-4 opacity-50" />
            </div>

            <div v-if="c.rows.length && open[c.key]" class="mt-3">
              <p class="hv2-sec-sub mb-2">{{ c.help }}</p>
              <hv-table :rows="c.rows" :row-key="c.rowKey || 'apv_code'">
                <template slot="thead">
                  <vs-th v-for="(h, hi) in c.headers" :key="h" :class="numericCol(c, hi) ? 'hv2-col-n' : ''">{{ h }}</vs-th>
                  <vs-th v-if="c.key === 'twins'"></vs-th>
                </template>
                <template slot="row" slot-scope="{ r }">
                  <vs-td v-for="(cell, ci) in c.cells(r)" :key="ci"
                         :class="cell.n ? 'hv2-col-n' : ''">
                    <router-link v-if="cell.link" :to="parcelLink(r)">{{ cell.v }}</router-link>
                    <span v-else>{{ cell.v }}</span>
                  </vs-td>
                  <vs-td v-if="c.key === 'twins'">
                    <vs-button size="small" type="border" color="warning" @click="openMerge(r)">
                      Fusionner
                    </vs-button>
                  </vs-td>
                </template>
              </hv-table>
            </div>
          </div>
        </div>
      </hv-section>
    </vx-card>

    <!-- ===================== JOURNAL DE SYNCHRO ===================== -->
    <vx-card class="mb-base">
      <hv-section
        title="Journal de la synchro SUMAL"
        sub="Une ligne par exécution de SumalX2n, qui reprend à 6 h, 12 h et 18 h les fiches dépôt et le stock déclaré. Sans ce journal, une synchro qui s'arrête est indiscernable d'une nuit sans changement.">
        <hv-table
          :rows="runs"
          :loading="runsLoading"
          row-key="id"
          empty-icon="ClockIcon"
          empty-text="Aucune exécution enregistrée pour le moment.">
          <template slot="thead">
            <vs-th>Exécution</vs-th><vs-th>Résultat</vs-th><vs-th class="hv2-col-n">Durée</vs-th>
            <vs-th class="hv2-col-n">Dépôts</vs-th><vs-th class="hv2-col-n">Nouveaux</vs-th>
            <vs-th class="hv2-col-n">Parcelles créées</vs-th><vs-th class="hv2-col-n">Mises à jour</vs-th><vs-th class="hv2-col-n">À encoder</vs-th>
          </template>
          <template slot="row" slot-scope="{ r }">
            <vs-td>{{ dateTime(r.started_at) }}</vs-td>
            <vs-td>
              <hv-pill v-if="r.ok" label="réussie" tone="ok" icon="CheckIcon" />
              <hv-pill v-else :label="r.error ? 'échec' : 'inachevée'" tone="danger" icon="AlertCircleIcon"
                       :title="r.error || ''" />
            </vs-td>
            <vs-td class="hv2-col-n">{{ runDuration(r) }}</vs-td>
            <vs-td class="hv2-col-n">{{ int(r.depots_upserted) }}</vs-td>
            <vs-td class="hv2-col-n">{{ int(r.depots_new) }}</vs-td>
            <vs-td class="hv2-col-n">{{ int(r.parcels_created) }}</vs-td>
            <vs-td class="hv2-col-n">{{ int(r.parcels_updated) }}</vs-td>
            <vs-td class="hv2-col-n">{{ int(r.pending) }}</vs-td>
          </template>
        </hv-table>
      </hv-section>
    </vx-card>

    <!-- Fusion des produits jumeaux. Toujours montée : Vuesax déplace la popup
         dans document.body et un v-if la détruirait en figeant la page. -->
    <vs-popup title="Fusionner les produits d'un APV" :active.sync="mergeOpen">
      <div v-if="mergeTarget">
        <p class="hv2-sec-sub mb-4">
          L'APV <b>{{ mergeTarget.apv_code }}</b> porte {{ mergeTarget.products }} produits.
          Son volume est donc éclaté, et seul celui qui porte la réception entre dans les totaux
          de l'écran historique. La fusion déplace toutes les lignes de document vers un produit
          unique. Les lignes déplacées sont nommées dans le journal d'audit et le geste est
          réversible par identifiant.
        </p>
        <vs-select v-model="mergeKeep" label="Produit à conserver" class="w-full mb-4">
          <vs-select-item v-for="id in (mergeTarget.idproducts || [])" :key="id"
                          :value="id" :text="`Produit ${id}`" />
        </vs-select>
        <p class="hv2-sec-sub mb-4">
          Les autres produits seront vidés de leurs lignes, sans être supprimés.
        </p>
        <vs-button color="warning" :disabled="!mergeKeep || merging" @click="doMerge">
          {{ merging ? 'Fusion…' : 'Fusionner' }}
        </vs-button>
        <vs-button type="flat" class="ml-2" @click="mergeOpen = false">Annuler</vs-button>
      </div>
    </vs-popup>

    <!-- ===================== RÉCONCILIATION ===================== -->
    <vx-card>
      <hv-section
        title="Le test d'acceptation"
        sub="Le critère de réussite du module : l'écart entre le volume connu de XYLO et celui déclaré à SUMAL doit tendre vers zéro. Agrégé sur le code APV, jamais sur un produit.">
        <template slot="actions">
          <vs-button size="small" type="border" :disabled="checking" @click="runCheck">
            {{ checking ? 'Calcul…' : 'Lancer le contrôle' }}
          </vs-button>
        </template>

        <hv-empty v-if="!recon && !checking" icon="PlayCircleIcon"
                  text="Le contrôle n'a pas encore été lancé sur cette page." />
        <div v-else-if="checking" class="hv2-loading">
          <vs-progress indeterminate color="primary" class="mb-2" />Agrégation en cours…
        </div>
        <div v-else>
          <p class="hv2-sec-sub mb-3">
            Période du {{ date(recon.period.from) }} au {{ date(recon.period.cutoff) }}.
          </p>
          <div class="hv2-stats">
            <div class="vx-col w-full sm:w-1/2 lg:w-1/4">
              <hv-stat tier="tile" :value="m3int(recon.aggregated_by_apv_code_m3)" unit="m³"
                       label="agrégés par code APV" />
            </div>
            <div class="vx-col w-full sm:w-1/2 lg:w-1/4">
              <hv-stat tier="tile" :value="m3int(recon.sumal_declared_m3)" unit="m³"
                       label="déclarés à SUMAL" />
            </div>
            <div class="vx-col w-full sm:w-1/2 lg:w-1/4">
              <hv-stat tier="tile" :value="m3int(recon.difference_m3)" unit="m³" label="d'écart"
                       :tone="Math.abs(recon.difference_m3) > 300 ? 'warn' : ''"
                       :sub="recon.acceptance || ''" />
            </div>
            <div class="vx-col w-full sm:w-1/2 lg:w-1/4">
              <hv-stat tier="tile" :value="m3int(recon.without_reception_m3)" unit="m³"
                       label="invisibles à l'écran historique"
                       sub="Faute de réception : c'est ce que la refonte a rendu visible." />
            </div>
          </div>
          <ul v-if="recon.known_residual_lines && recon.known_residual_lines.length"
              class="hv2-disc-body">
            <li v-for="(l, i) in recon.known_residual_lines" :key="i">
              {{ l.label }} — {{ m3(l.m3) }} m³
            </li>
          </ul>
        </div>
      </hv-section>
    </vx-card>
  </div>
</template>

<script>
import axios from '@/axios.js'
import formatMixin from './formatMixin'
import { WEIGHBRIDGE_LIVE } from './devStatus'
import HvSection from './components/HvSection'
import HvPill from './components/HvPill'
import HvTable from './components/HvTable'
import HvEmpty from './components/HvEmpty'
import HvStat from './components/HvStat'

export default {
  mixins: [formatMixin],
  components: { HvSection, HvPill, HvTable, HvEmpty, HvStat },
  data () {
    return {
      loading: true,
      error: '',
      q: {},
      runs: [],
      runsLoading: true,
      recon: null,
      checking: false,
      open: {},
      mergeOpen: false,
      mergeTarget: null,
      mergeKeep: null,
      merging: false
    }
  },
  computed: {
    checks () {
      const q = this.q
      const list = [
        {
          key: 'nocode',
          label: 'Chaque produit « bois sur pied » porte un code APV',
          help: "Tous les regroupements du module s'appuient sur le code à 13 chiffres contenu dans le nom du produit. Un produit qui n'en porte pas est invisible partout ailleurs ; son volume est compté ici pour qu'il ne disparaisse jamais en silence.",
          rows: q.products_without_code || [],
          rowKey: 'id',
          headers: ['Produit', 'Nom', 'm³ sortis'],
          cells: (r) => [{ v: r.id }, { v: r.name_en }, { v: this.m3(r.m3), n: true }]
        },
        {
          key: 'twins',
          label: 'Un APV ne porte qu\'un seul produit',
          help: "Le volume de l'APV est éclaté entre plusieurs produits ; l'écran historique ne comptait que celui portant la réception. La fusion se lance depuis le détail de la parcelle.",
          rows: q.twins || [],
          headers: ['APV', 'Produits', 'Avize', 'm³ sortis', 'Réceptions'],
          cells: (r) => [
            { v: r.apv_code, link: true }, { v: (r.idproducts || []).join(', ') },
            { v: this.int(r.avize), n: true }, { v: this.m3(r.m3_out), n: true },
            { v: this.int(r.receptions), n: true }
          ]
        },
        {
          key: 'oversized',
          label: 'Aucun produit ne sert de fourre-tout',
          help: "Il est sorti bien plus de bois que la réception n'en a acheté. Possiblement un produit partagé par plusieurs parcelles — auquel cas son coût par parcelle ne veut rien dire. À examiner, pas à conclure.",
          rows: q.oversized || [],
          headers: ['APV', 'Parcelle', 'm³ achetés', 'm³ sortis', 'Avize'],
          cells: (r) => [
            { v: r.apv_code, link: true }, { v: this.parcelName(r) },
            { v: this.m3(r.reception_m3), n: true }, { v: this.m3(r.m3_out), n: true },
            { v: this.int(r.avize), n: true }
          ]
        },
        {
          key: 'tonnage',
          label: 'Les tonnages saisis sont lisibles',
          help: "Le champ tonnage est du texte libre : une valeur que l'on ne sait pas interpréter est écartée de tous les calculs de densité et de course.",
          rows: q.unparseable_tonnage || [],
          rowKey: 'id',
          headers: ['Opération', 'Date', 'Aviz', 'Valeur saisie'],
          cells: (r) => [{ v: r.id }, { v: this.date(r.date) }, { v: r.aviz }, { v: r.transport_tonaj }]
        }
      ]

      // Le contrôle « avize sans pesée » ne veut rien dire tant que la bascule
      // est arrêtée : il signalerait 80 % des avize et noierait les autres.
      if (WEIGHBRIDGE_LIVE) {
        list.push({
          key: 'weighing',
          label: 'Chaque aviz a sa pesée',
          help: 'Sans pesée, la course est estimée par plaque et par jour, et la densité ne se calcule pas.',
          rows: q.avize_without_weighing || [],
          headers: ['APV', 'Avize sans pesée', 'Avize'],
          cells: (r) => [
            { v: r.apv_code, link: true },
            { v: this.int(r.avize_without_weighing), n: true },
            { v: this.int(r.avize), n: true }
          ]
        })
      }
      return list
    },
    failing () { return this.checks.filter((c) => c.rows.length).length }
  },
  methods: {
    toggle (key) { this.$set(this.open, key, !this.open[key]) },
    // Le titre suit l'alignement de sa colonne : chiffres à droite, titre à droite.
    numericCol (c, i) {
      const cell = c.cells(c.rows[0])[i]
      return !!(cell && cell.n)
    },
    openMerge (r) {
      this.mergeTarget = r
      // On propose par défaut le produit qui porte la réception : c'est celui
      // que les totaux historiques voyaient déjà.
      this.mergeKeep = (r.idproducts || [])[0] || null
      this.mergeOpen = true
    },
    doMerge () {
      if (!this.mergeKeep || !this.mergeTarget) return
      this.merging = true
      const sources = (this.mergeTarget.idproducts || []).filter((id) => id !== this.mergeKeep)
      axios.post('/harvesting2/merge-products', {
        apv_code: this.mergeTarget.apv_code,
        target_idproduct: this.mergeKeep,
        source_idproducts: sources
      })
        .then((r) => {
          this.$vs.notify({
            title: 'Fusion effectuée',
            text: `${(r.data && r.data.moved_lines) || 0} ligne(s) déplacée(s) vers le produit ${this.mergeKeep}.`,
            iconPack: 'feather', icon: 'icon-check', color: 'success',
            position: 'bottom-right', time: 6000
          })
          this.mergeOpen = false
          this.load()
        })
        .catch((e) => {
          this.$vs.notify({
            title: 'Fusion impossible',
            text: (e.response && e.response.data && e.response.data.message) || e.message,
            iconPack: 'feather', icon: 'icon-alert-circle', color: 'danger',
            position: 'bottom-right', time: 6000
          })
        })
        .finally(() => { this.merging = false })
    },
    runDuration (r) {
      if (!r.started_at || !r.finished_at) return this.DASH
      const ms = new Date(r.finished_at) - new Date(r.started_at)
      return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`
    },
    runCheck () {
      this.checking = true
      axios.get('/harvesting2/reconciliation')
        .then((r) => { this.recon = r.data })
        .catch((e) => {
          this.$vs.notify({
            title: 'Contrôle impossible',
            text: (e.response && e.response.data && e.response.data.message) || e.message,
            iconPack: 'feather', icon: 'icon-alert-circle', color: 'danger',
            position: 'bottom-right', time: 6000
          })
        })
        .finally(() => { this.checking = false })
    },
    load () {
      axios.get('/harvesting2/quality')
        .then((r) => { this.q = r.data || {} })
        .catch((e) => {
          this.error = (e.response && e.response.data && e.response.data.message) || e.message || 'Erreur inconnue'
        })
        .finally(() => { this.loading = false })

      axios.get('/sumal2/runs?limit=10')
        .then((r) => { this.runs = r.data || [] })
        .catch(() => { this.runs = [] })
        .finally(() => { this.runsLoading = false })
    }
  },
  created () { this.load() }
}
</script>
